import type { D20Face, GameEvent, GameState, Player } from '@rookandroll/engine';
import { squareName } from '@rookandroll/engine';

const PIECE = { K: 'king', Q: 'queen', R: 'rook', B: 'bishop', N: 'knight', P: 'pawn' } as const;
const FACE: Record<D20Face, string> = { '1': 'a 1', '2': 'a 2', '3': 'a 3', skip: 'Miss a go', reverse: 'Reverse' };

/** Plain-English log lines for the events of one action. `before` is the state the action was applied to. */
export function describe(events: GameEvent[], before: GameState, human: Player): string[] {
  const who = (p: Player) => (p === human ? 'You' : 'The bot');
  const verb = (p: Player, you: string, bot: string) => (p === human ? you : bot);
  const out: string[] = [];
  for (const e of events) {
    switch (e.type) {
      case 'rolled':
        out.push(`${who(e.player)} ${verb(e.player, 'rolled', 'rolled')} ${FACE[e.face]}${/^\d$/.test(e.face) ? (e.face === '1' ? ': one move.' : `: ${e.face} moves, at most one heavy move.`) : '.'}`);
        break;
      case 'skipped':
        out.push('Turn passes.');
        break;
      case 'reverse-level':
        out.push('Material is level, so nobody is worse off: the reverse goes through.');
        break;
      case 'reverse-pending':
        out.push(`The reverse would hurt ${e.hurt === human ? 'you' : 'the bot'} (ahead on material).`);
        break;
      case 'save-rolled':
        out.push(`${who(e.player)} rolled ${e.face === 'save' ? 'a save' : 'a blank'}.`);
        break;
      case 'veto-used':
        out.push(`${who(e.player)} used the veto.`);
        break;
      case 'reverse-cancelled':
        out.push('Reverse cancelled. Turn passes.');
        break;
      case 'reversed':
        out.push('Reverse! Control of the pieces swaps. Turn passes.');
        break;
      case 'moved': {
        if (e.move.kind === 'add') {
          out.push(`${who(e.player)} added a pawn on ${squareName(e.move.to)} instead of a move.`);
          break;
        }
        const piece = PIECE[before.board[e.move.from]!.t];
        out.push(`${who(e.player)} ${verb(e.player, 'moved', 'moved')} the ${piece} ${squareName(e.move.from)} to ${squareName(e.move.to)}${e.captured ? `, capturing a ${PIECE[e.captured]}` : ''}.`);
        break;
      }
      case 'no-legal-moves':
        out.push(e.player === human ? 'You have no legal move left; the rest of the turn is lost.' : 'The bot has no legal move left; the rest of its turn is lost.');
        break;
      case 'game-over':
        out.push(e.winner === human ? 'King captured. You win!' : 'King captured. The bot wins.');
        break;
    }
  }
  return out;
}
